import { CompanyAvatar } from '@/components/company-avatar';
import { companiesApi } from '@/lib/api';
import { formatMatrixDate } from '@/lib/matrix-grid-filters';
import type { ArchivedCompany } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const RESTORE_COMPANY_ERROR_MESSAGE = 'La restauration a échoué. Réessayez.';

export interface ArchivedCompanyCardProps {
  company: ArchivedCompany;
  /** Opens the permanent-deletion dialog for this company. */
  onDelete: (company: ArchivedCompany) => void;
}

/**
 * One card of the archives page (issue #20, planche 03): greyed out and not a
 * link — only its two buttons act. « Restaurer » needs no confirmation, since
 * archiving again undoes it; « Supprimer définitivement » hands over to the
 * confirmation dialog.
 */
export function ArchivedCompanyCard({ company, onDelete }: ArchivedCompanyCardProps) {
  const queryClient = useQueryClient();
  const restoreMutation = useMutation({
    mutationFn: () => companiesApi.restore(company.id),
    onSuccess: () => {
      // The company page may be cached in its archived state.
      void queryClient.invalidateQueries({ queryKey: ['company', company.id] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  return (
    <div
      data-company-id={company.id}
      className="flex h-full flex-col items-center gap-3 rounded-lg border-2 border-gray-100 bg-gray-50 p-6 text-center"
    >
      <CompanyAvatar company={company} className="opacity-55 grayscale" />
      <span className="font-semibold text-gray-700">{company.name}</span>
      <span className="-mt-2 text-caption text-gray-500">
        Archivée le {formatMatrixDate(company.archivedAt)}
      </span>
      <div className="mt-1 flex w-full flex-col gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={restoreMutation.isPending}
          onClick={() => restoreMutation.mutate()}
        >
          <Icon name="unarchive" size={16} />
          {restoreMutation.isPending ? 'Restauration…' : 'Restaurer'}
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={restoreMutation.isPending}
          onClick={() => onDelete(company)}
        >
          <Icon name="delete" size={16} />
          Supprimer définitivement
        </Button>
      </div>
      {restoreMutation.isError && (
        <p role="alert" className="font-semibold text-rouge-deep text-small">
          {RESTORE_COMPANY_ERROR_MESSAGE}
        </p>
      )}
    </div>
  );
}
